'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { CheckCircle2, ArrowDown, TrendingUp } from 'lucide-react';

interface Plan {
  name: string;
}

interface HowItWorksSectionProps {
  plan: Plan;
}

export function HowItWorksSection({ plan }: HowItWorksSectionProps) {
  const t = useTranslations('planDetails.emgoldexPlan.howItWorks');

  const steps = [
    {
      number: '01',
      title: t('steps.step1.title'),
      description: t('steps.step1.description'),
      icon: CheckCircle2,
    },
    {
      number: '02',
      title: t('steps.step2.title'),
      description: t('steps.step2.description'),
      icon: ArrowDown,
    },
    {
      number: '03',
      title: t('steps.step3.title'),
      description: t('steps.step3.description'),
      icon: TrendingUp,
    },
  ];

  return (
    <SectionWrapper variant="default" padding="md" className="relative">
      <div className="container">
        <div className="text-center mb-12">
          <Typography variant="h2" className="mb-4">
            {t('title')}
          </Typography>
          <Typography variant="p" className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
            {t('description')}
          </Typography>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="relative"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border-2 border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600 transition-all duration-300 hover:shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <span className="text-4xl font-bold text-primary-200 dark:text-primary-800">{step.number}</span>
                  </div>
                  <Typography variant="h3" className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </Typography>
                  <Typography variant="p" className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {step.description}
                  </Typography>
                </div>
                {
                  index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowDown className="w-8 h-8 text-primary-400 rotate-[270deg]" />
                    </div>
                  )
                }
              </div>
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
}

